import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import SectionDivider from './components/SectionDivider';
import About from './components/About';
import Skills from './components/Skills';
import Services from './components/Services';
import CostCalculator from './components/CostCalculator';
import Portfolio from './components/Portfolio';
import AiVideoShowcase from './components/AiVideoShowcase';
import Testimonials from './components/Testimonials';
import WhyChooseMe from './components/WhyChooseMe';
import Contact from './components/Contact';
import Footer from './components/Footer';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import InstallBanner from './components/InstallBanner';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import { LanguageProvider, useLanguage } from './context/LanguageContext';

const AppContent: React.FC = () => {
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const { t, dir } = useLanguage();

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Global Smooth Scroll for all anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');

      if (anchor && anchor.hash && anchor.origin === window.location.origin) {
        const targetId = anchor.hash;
        if (targetId === '#' || targetId === '#home') {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          e.preventDefault();
          const navOffset = 80;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - navOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    };

    window.addEventListener('click', handleAnchorClick);
    return () => window.removeEventListener('click', handleAnchorClick);
  }, []);

  // Performance-optimized Animation Observer with generous rootMargin
  useEffect(() => {
    const observerOptions = {
      threshold: 0.01,
      rootMargin: '200px 0px 100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const revealElements = document.querySelectorAll('.reveal, .section-slide-up');
    revealElements.forEach(el => observer.observe(el));

    // Ensure elements close to the top are active immediately
    const immediateElements = document.querySelectorAll('.near-fold');
    immediateElements.forEach(el => el.classList.add('active'));

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div 
      dir={dir}
      className={`min-h-screen w-full font-sans ${darkMode ? 'bg-[#020617] text-slate-100' : 'bg-white text-slate-900'} selection:bg-violet-500 selection:text-white overflow-x-hidden transition-colors duration-500`}
    >
      <Navbar />
      <main className="w-full">
        <Hero />
        <div className="section-slide-up near-fold active">
          <SectionDivider sectionName={t.sectionDividers.about} />
        </div>
        <div className="section-slide-up near-fold active">
          <About />
        </div>
        <div className="section-slide-up">
          <SectionDivider sectionName={t.sectionDividers.skills} />
        </div>
        <div className="section-slide-up">
          <Skills />
        </div>
        <div className="section-slide-up">
          <SectionDivider sectionName={t.sectionDividers.services} />
        </div>
        <div className="section-slide-up">
          <Services />
        </div>
        <div className="section-slide-up">
          <SectionDivider sectionName={t.sectionDividers.estimator} />
        </div>
        <div className="section-slide-up">
          <CostCalculator />
        </div>
        <div className="section-slide-up">
          <SectionDivider sectionName={t.sectionDividers.portfolio} />
        </div>
        <div className="section-slide-up">
          <Portfolio />
        </div>
        <div className="section-slide-up">
          <SectionDivider sectionName={t.sectionDividers.aiVideos} />
        </div>
        <div className="section-slide-up">
          <AiVideoShowcase />
        </div>
        <div className="section-slide-up">
          <SectionDivider sectionName={t.sectionDividers.testimonials} />
        </div>
        <div className="section-slide-up">
          <Testimonials />
        </div>
        <div className="section-slide-up">
          <SectionDivider sectionName={t.sectionDividers.whyChooseMe} />
        </div>
        <div className="section-slide-up">
          <WhyChooseMe />
        </div>
        <div className="section-slide-up">
          <SectionDivider sectionName={t.sectionDividers.contact} />
        </div>
        <div className="section-slide-up">
          <Contact />
        </div>
        <Footer />
        <FloatingWhatsApp />
        <InstallBanner />
        <KeyboardShortcuts />
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
};

export default App;